/**
 * Feature flags utility for controlling functionality based on environment variables
 * and backend service availability
 */

export const featureFlags = {
  /**
   * Controls whether WebPubSub real-time notifications are enabled
   * Requires backend WebPubSub service to be running
   */
  webPubSubEnabled: import.meta.env.VITE_WEBPUBSUB_ENABLED === "true",

  /**
   * Controls whether the upload service is enabled for document uploads
   * This enables the new /documents/upload streaming endpoint
   * Backend handles blob storage availability
   */
  uploadEnabled: import.meta.env.VITE_UPLOAD_SERVICE_ENABLED === "true",

  /**
   * Controls whether real-time notifications are available
   * This is specifically for WebPubSub notifications
   */
  get notificationsEnabled() {
    return this.webPubSubEnabled;
  },

  /**
   * Controls whether task templates are enabled
   * Requires backend task-templates API endpoint and functionality
   */
  taskTemplatesEnabled: import.meta.env.VITE_TASK_TEMPLATES_ENABLED === "true",

  /**
   * Controls whether the destructive "delete document + related records" cascade
   * is offered in the document delete-impact dialog (#5751). OFF until the write
   * cascade (#5753) and the destructive-cascade capability (#5752) land. Enabling
   * the button must gate on BOTH this flag AND the real capability check (#5752) —
   * not this flag alone.
   */
  cascadeDocumentDeleteEnabled: import.meta.env.VITE_CASCADE_DOCUMENT_DELETE_ENABLED === "true",

  /**
   * Offers the Conditional laser type on claimant create and policy create
   * (Feature #3502). Those forms have no claimant to ask the API about, so,
   * unlike the laser dialog and the claimant page (which detect the
   * conditional-laser routes at runtime), they can't tell whether the API has
   * them. Turn on per environment once coronis-apps !4876/!4877 are deployed
   * there; until then a Conditional create saves the claimant and then fails
   * its condition writes (!4880 thread 30533). A getter, so tests can stub the
   * env.
   */
  get conditionalLasersEnabled() {
    return import.meta.env.VITE_CONDITIONAL_LASERS_ENABLED === "true";
  },

  /**
   * Controls whether the Aggregate Deductible tab and calculations are enabled
   * Requires backend aggregate deductible endpoints to be available
   */
  aggregateCalculationsEnabled: import.meta.env.VITE_AGGREGATE_CALCULATIONS_ENABLED === "true",

  /**
   * Controls multi-sheet workbook (MSW) handling in the document dropzone:
   * client-side sheet discovery, oversized/password-protected/unparseable
   * rejection, sheetNames population on FileAssignment, and the sheet
   * picker UI. When disabled the dropzone falls back to pre-MSW
   * behavior — single upload per file, no client-side workbook gating.
   */
  multiSheetWorkbookEnabled: import.meta.env.VITE_MULTI_SHEET_WORKBOOK_ENABLED === "true",

  /**
   * Controls whether the policy document ingestion flow (ADR-042) is
   * visible to users. When disabled, the "Policy" option is hidden from
   * the documents-page upload dialog so users cannot upload policy
   * documents through the extraction pipeline — currently incomplete
   * pending the v1 stories landing together.
   *
   * Note: PolicyPdfUpload on the policy show page is NOT gated by this
   * flag. That flow uses storage_only=true and is intended for
   * attaching reference PDFs to existing policies, not for ingestion.
   *
   * Pair with backend flag `policy_ingestion_v1` in worker-api config —
   * both should flip on together once the full v1 set is in production.
   */
  policyIngestionEnabled: import.meta.env.VITE_POLICY_INGESTION_ENABLED === "true",

  /**
   * Master flag for the document-processing review & remap UX overhaul
   * (Feature #6097): the persistent review context bar (#6099) and the
   * source-mirrored field-mapping / remap dialog (#6101). The existing
   * review summary (schema tabs) is intentionally kept as-is. When disabled
   * the prior review surface renders unchanged. On in every deployed
   * environment; retained only as a kill switch.
   */
  documentReviewRedesignEnabled: import.meta.env.VITE_DOCUMENT_REVIEW_REDESIGN_ENABLED === "true",

  /**
   * Controls whether the claimant Documents "Add attachment" uploader is offered
   * (#6506). The storage-only claimant upload path has a HARD backend dependency:
   * it needs apps !4532 (#6553 vendor-less storage-only upload + #6557
   * storage_only-preserving finalize) DEPLOYED, not merely merged — FE and BE
   * ship independently, so merge order alone doesn't protect a mis-sequenced
   * deploy. OFF until the backend is live in each environment. The panel (list +
   * preview) renders regardless; only the add affordance is gated.
   */
  claimantAttachmentsEnabled: import.meta.env.VITE_CLAIMANT_ATTACHMENTS_ENABLED === "true",

  /**
   * Mirrors the backend's deductible-basis switch (ADR-081 D5, coronis-apps
   * CORONIS_FEATURE_DEDUCTIBLE_BASIS_VIEW). With it on, a claim line's eligibility
   * stamp is derived from the policy's contract windows on every load and edit, so
   * the claim-line page hides "Mark Eligible" (a manual flip is overwritten by the
   * restamp in the same request) and the ineligibility banner explains the reason
   * from the stamp instead (#7338 minimum slice, flag-on prerequisite for #7321).
   * Build-time, so it can never flip atomically with the backend's runtime flag. Order
   * matters (!4855 thread 29976): turn the BACKEND on first, then deploy the SPA build
   * with this on; roll back in reverse (SPA off first, then the backend). SPA-on with
   * the backend off removes Mark Eligible while flag-off spend still reads is_eligible;
   * backend-on with the SPA off only shows an action the restamp overwrites.
   */
  deductibleBasisViewEnabled: import.meta.env.VITE_DEDUCTIBLE_BASIS_VIEW_ENABLED === "true",
} as const;

/**
 * Helper function to check if a feature is enabled
 * @param feature - The feature flag to check
 * @returns boolean indicating if the feature is enabled
 */
export const isFeatureEnabled = (feature: keyof typeof featureFlags): boolean => {
  return Boolean(featureFlags[feature]);
};

/**
 * Helper function to get feature flag status for debugging
 * @returns Object with all feature flag states
 */
export const getFeatureFlagsStatus = () => {
  return {
    webPubSub: featureFlags.webPubSubEnabled,
    upload: featureFlags.uploadEnabled,
    notifications: featureFlags.notificationsEnabled,
    taskTemplates: featureFlags.taskTemplatesEnabled,
    aggregateCalculations: featureFlags.aggregateCalculationsEnabled,
    multiSheetWorkbook: featureFlags.multiSheetWorkbookEnabled,
    policyIngestion: featureFlags.policyIngestionEnabled,
    documentReviewRedesign: featureFlags.documentReviewRedesignEnabled,
    claimantAttachments: featureFlags.claimantAttachmentsEnabled,
    deductibleBasisView: featureFlags.deductibleBasisViewEnabled,
  };
};
